import { formatPoints } from '../lib/decimal'
import type { PointSchemeView, RulesetView } from '../api/types'

/**
 * A season's rules in a sentence, then in a table.
 *
 * "Best 11 of 16" is the whole reason some of these championships went the way
 * they did, and a reader should not have to decode `BEST_N` with `n: 11` to
 * learn it.
 */
export function DropRuleProse({ ruleset, rounds }: { ruleset: RulesetView; rounds?: number }) {
  return <>{dropSentence(ruleset, rounds)}</>
}

export function dropSentence(ruleset: RulesetView, rounds?: number): string {
  const counting =
    rounds === undefined ? undefined : rounds - (ruleset.excluded_rounds ?? []).length
  switch (ruleset.best_rounds_mode) {
    case 'ALL':
      return counting === undefined
        ? 'Every round counted.'
        : `Every round counted, all ${counting} of them.`
    case 'ALL_BUT_ONE':
      return 'Every round counted but one: the worst result was discarded.'
    case 'BEST_N':
      return counting === undefined
        ? `Only a competitor’s best ${ruleset.best_rounds_n} results counted.`
        : `Only a competitor’s best ${ruleset.best_rounds_n} of ${counting} results counted.`
    case 'SEGMENTS':
      return 'The season was split into halves, each with its own limit on counting results.'
    default:
      return 'Every round counted.'
  }
}

export function PointsRow({ scheme }: { scheme: PointSchemeView }) {
  const positions = Object.entries(scheme.positions)
    .map(([place, points]) => [Number(place), points] as const)
    .sort((a, b) => a[0] - b[0])

  return (
    <div className="scheme">
      <div className="scheme__head">
        <code>{scheme.key}</code>
        {scheme.description ? <span className="muted"> {scheme.description}</span> : null}
      </div>
      <ol className="scheme__points">
        {positions.map(([place, points]) => (
          <li key={place}>
            <span className="scheme__place">{place}</span>
            <span className="scheme__value">{formatPoints(points)}</span>
          </li>
        ))}
      </ol>
      <p className="muted scheme__fl">{fastestLapSentence(scheme)}</p>
    </div>
  )
}

export function fastestLapSentence(scheme: PointSchemeView): string {
  const rule = scheme.fastest_lap
  const points = formatPoints(rule.points, '0')
  switch (rule.rule) {
    case 'NONE':
      return 'No point for the fastest lap.'
    case 'ANY':
      return `${points} for the fastest lap, wherever it finished.`
    case 'SHARED':
      return `${points} for the fastest lap, split between everyone who set the same time.`
    case 'TOP_N':
      return `${points} for the fastest lap, if it finished in the top ${rule.max_position ?? 10}.`
    default:
      return ''
  }
}

const PER_SESSION: Record<string, string> = {
  SUM_POINTS: 'Every car’s points counted.',
  BEST_POINTS: 'Only a team’s best-placed car scored.',
}

const ELIGIBILITY: Record<string, string> = {
  ALL: 'Everyone who entered appears in the table.',
  HAS_POINT: 'Only competitors who scored appear in the table.',
  HAS_FINISH: 'Only competitors who were classified appear in the table.',
  HAS_FINISH_OR_QUALI: 'Only competitors who qualified or were classified appear.',
}

const TIE_BREAK: Record<string, string> = {
  NONE: 'Ties were left as ties.',
  COUNTBACK_ALL: 'Ties were broken by the better finishing positions, counting every race.',
  COUNTBACK_COUNTED:
    'Ties were broken by the better finishing positions among counting results.',
}

export function ruleNotes(ruleset: RulesetView, rounds?: number): string[] {
  const notes = [dropSentence(ruleset, rounds)]
  const perSession = PER_SESSION[ruleset.per_session ?? '']
  if (perSession) notes.push(perSession)
  const eligibility = ELIGIBILITY[ruleset.eligibility ?? '']
  if (eligibility) notes.push(eligibility)
  const tieBreak = TIE_BREAK[ruleset.tie_break ?? '']
  if (tieBreak) notes.push(tieBreak)
  if ((ruleset.excluded_rounds ?? []).length > 0) {
    notes.push(
      `Rounds ${ruleset.excluded_rounds?.join(', ')} did not count towards this championship.`,
    )
  }
  const multipliers = Object.entries(ruleset.round_multipliers ?? {})
  if (multipliers.length > 0) {
    notes.push(
      `Some rounds scored differently: ${multipliers
        .map(([round, factor]) => `round ${round} at ${formatPoints(factor)}×`)
        .join(', ')}.`,
    )
  }
  if (ruleset.notes) notes.push(ruleset.notes)
  return notes
}
